import { useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Plus, Server, Shield, ShieldCheck } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"

import { AccessPolicyDialog } from "./access-policy-dialog"
import { AddMcpDialog } from "./add-mcp-dialog"
import { CollapsibleManagementSection } from "./collapsible-management-section"
import { EditMcpDialog } from "./edit-mcp-dialog"
import { McpsTable } from "./mcps-table"
import {
  useAddMcpServerMutation,
  useAdminAgentMcpsQuery,
  useAdminAgentsQuery,
  useAdminMcpAccessPolicyQuery,
  useAdminMcpsBaseAccessPolicyQuery,
  useClearMcpAccessPolicyMutation,
  useDeleteMcpServerMutation,
  useUpdateMcpAccessPolicyMutation,
  useUpdateMcpsBaseAccessPolicyMutation,
  useUpdateMcpServerMutation,
  useUpdateMcpStatusMutation,
} from "../hooks/admin-queries"
import { isPolicyConfigured } from "../utils/access-policy"

export function McpsManagementCard() {
  const { t } = useTranslation()
  const [updatingLabel, setUpdatingLabel] = useState<string | undefined>()
  const [deletingLabel, setDeletingLabel] = useState<string | undefined>()
  const [isUpdatingAll, setIsUpdatingAll] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState<string>("explorer")
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false)
  const [accessMcpLabel, setAccessMcpLabel] = useState<string | undefined>()
  const [isBaseAccessDialogOpen, setIsBaseAccessDialogOpen] = useState(false)
  const [editingLabel, setEditingLabel] = useState<string | null>(null)

  const updateMcpMutation = useUpdateMcpServerMutation(selectedAgent)

  const { data: agents, isLoading: isLoadingAgents } = useAdminAgentsQuery()
  const { data: mcps, isLoading: isLoadingMcps } = useAdminAgentMcpsQuery(selectedAgent)
  const {
    data: baseAccessPolicy,
    isLoading: isLoadingBaseAccessPolicy,
    isError: isBaseAccessPolicyError,
  } = useAdminMcpsBaseAccessPolicyQuery(selectedAgent, true)
  const updateMutation = useUpdateMcpStatusMutation()
  const deleteMutation = useDeleteMcpServerMutation()
  const addMutation = useAddMcpServerMutation()
  const updateAccessMutation = useUpdateMcpAccessPolicyMutation()
  const clearAccessMutation = useClearMcpAccessPolicyMutation()
  const updateBaseAccessMutation = useUpdateMcpsBaseAccessPolicyMutation()
  const {
    data: mcpAccessPolicy,
    isLoading: isLoadingAccessPolicy,
    isError: isAccessPolicyError,
  } = useAdminMcpAccessPolicyQuery(selectedAgent, accessMcpLabel, isAccessDialogOpen)
  const queryClient = useQueryClient()

  const isLoading = isLoadingAgents || isLoadingMcps

  const handleToggleStatus = async (label: string, enabled: boolean) => {
    setUpdatingLabel(label)
    try {
      await updateMutation.mutateAsync({ agentName: selectedAgent, label, enabled })
    } catch {
      // Error handled by React Query mutation
    } finally {
      setUpdatingLabel(undefined)
    }
  }

  const handleToggleAll = async (enabled: boolean) => {
    if (!mcps || mcps.length === 0) return

    setIsUpdatingAll(true)

    await queryClient.cancelQueries({ queryKey: ["admin", "agents", selectedAgent, "mcps"] })

    const currentMcps =
      queryClient.getQueryData<typeof mcps>(["admin", "agents", selectedAgent, "mcps"]) || mcps

    const previousMcps = currentMcps
    const mcpsToUpdate = currentMcps.filter((mcp) => mcp.enabled !== enabled)

    queryClient.setQueryData(
      ["admin", "agents", selectedAgent, "mcps"],
      currentMcps.map((mcp) => ({ ...mcp, enabled }))
    )

    try {
      await Promise.all(
        mcpsToUpdate.map((mcp) =>
          updateMutation.mutateAsync({ agentName: selectedAgent, label: mcp.label, enabled })
        )
      )
    } catch {
      queryClient.setQueryData(["admin", "agents", selectedAgent, "mcps"], previousMcps)
      await queryClient.invalidateQueries({ queryKey: ["admin", "agents", selectedAgent, "mcps"] })
    } finally {
      setIsUpdatingAll(false)
    }
  }

  const allEnabledState = useMemo(() => {
    if (!mcps || mcps.length === 0) return false
    const enabledCount = mcps.filter((mcp) => mcp.enabled).length
    if (enabledCount === 0) return false
    if (enabledCount === mcps.length) return true
    return undefined
  }, [mcps])

  const handleDelete = async (label: string) => {
    setDeletingLabel(label)
    try {
      await deleteMutation.mutateAsync({ agentName: selectedAgent, label })
      toast.success(t("adminDashboard.mcps.deleteSuccess", "MCP server deleted"))
    } catch {
      toast.error(t("adminDashboard.mcps.deleteError", "Failed to delete MCP server"))
    } finally {
      setDeletingLabel(undefined)
    }
  }

  const handleAddMcp = async (config: {
    label: string
    url: string
    allowed_tools?: string[] | null
    require_approval: string
    enabled: boolean
  }) => {
    try {
      await addMutation.mutateAsync({ agentName: selectedAgent, config })
      toast.success(t("adminDashboard.mcps.addSuccess", "MCP server added"))
    } catch {
      toast.error(t("adminDashboard.mcps.addError", "Failed to add MCP server"))
      throw new Error("Failed to add MCP server")
    }
  }

  const selectedMcp = mcps?.find((m) => m.label === accessMcpLabel)
  const inheritByDefault = selectedMcp?.access_overridden !== true
  const baseAccessConfigured = useMemo(() => {
    return isPolicyConfigured(baseAccessPolicy)
  }, [baseAccessPolicy])

  return (
    <>
      <CollapsibleManagementSection
        icon={<Server className="size-5" />}
        id="mcps"
        storageKey="admin-management-mcps-expanded"
        title={t("adminDashboard.mcps.title", "MCP Server Management")}
        headerActions={
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Button
              className="flex items-center gap-2 px-3"
              size="sm"
              onClick={() => setIsAddDialogOpen(true)}
            >
              <Plus className="size-4" />
              {t("adminDashboard.mcps.addServer", "Add Server")}
            </Button>
            <Select
              disabled={isLoading || isUpdatingAll}
              value={selectedAgent}
              onValueChange={(value) => {
                setSelectedAgent(value)
                setIsAccessDialogOpen(false)
                setIsBaseAccessDialogOpen(false)
                setAccessMcpLabel(undefined)
              }}
            >
              <SelectTrigger aria-label="Agent" className="w-40">
                <SelectValue placeholder="Select agent" />
              </SelectTrigger>
              <SelectContent>
                {agents?.map((agent) => (
                  <SelectItem key={agent} value={agent}>
                    {agent}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              className="flex items-center gap-2 px-3"
              disabled={isLoading || isUpdatingAll}
              size="sm"
              type="button"
              variant="outline"
              onClick={() => setIsBaseAccessDialogOpen(true)}
            >
              {baseAccessConfigured ? (
                <ShieldCheck className="text-success size-4" />
              ) : (
                <Shield className="size-4" />
              )}
              {t("adminDashboard.mcps.baseAccess", { defaultValue: "Access for all" })}
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">
                {t("adminDashboard.mcps.enableAll", "Enable All")}
              </span>
              <Switch
                aria-label={t("adminDashboard.mcps.enableAll", "Enable All")}
                checked={allEnabledState === true}
                disabled={isLoading || isUpdatingAll || !mcps || mcps.length === 0}
                onCheckedChange={handleToggleAll}
              />
            </div>
          </div>
        }
      >
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 2 }).map((_, index) => (
              <div key={index} className="flex items-center justify-between rounded-lg border p-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-6 w-12" />
              </div>
            ))}
          </div>
        ) : (
          <McpsTable
            disableAll={isUpdatingAll}
            isDeleting={deletingLabel}
            isUpdating={updatingLabel}
            mcps={mcps || []}
            onDelete={handleDelete}
            onEdit={(label) => setEditingLabel(label)}
            onToggleStatus={handleToggleStatus}
            onEditAccess={(label) => {
              setAccessMcpLabel(label)
              setIsAccessDialogOpen(true)
            }}
          />
        )}
      </CollapsibleManagementSection>

      {editingLabel && mcps
        ? (() => {
            const target = mcps.find((m) => m.label === editingLabel)
            if (!target) return null
            // Strip UI-only metadata (source / access_*) so the Raw tab
            // sees only the fields that actually live in the stored Cosmos
            // entry. Everything else — headers and any forward-compatible
            // keys — is passed through so the Raw editor opens with the
            // true shape.
            const {
              source: _uiSource,

              access_configured: _uiAccessConfigured,

              access_overridden: _uiAccessOverridden,
              ...storedMcp
            } = target
            return (
              <EditMcpDialog
                isOpen
                agentName={selectedAgent}
                isPending={updateMcpMutation.isPending}
                mcp={storedMcp}
                onOpenChange={(open) => {
                  if (!open) setEditingLabel(null)
                }}
                onRawSave={async (next, reason, expectedHash) => {
                  try {
                    // Raw mode: forward every field the admin entered (minus
                    // the path-authoritative ``label``) so extras — current
                    // or forward-compatible — aren't silently dropped at the
                    // page-handler boundary. The backend ``update_mcp_server``
                    // also strips ``label`` as a rename guard, replaces the
                    // entry (so deleted keys actually disappear), and
                    // requires ``expected_hash`` for optimistic concurrency.
                    const rawUpdates: Record<string, unknown> = { ...next }
                    delete rawUpdates.label
                    await updateMcpMutation.mutateAsync({
                      label: target.label,
                      updates: rawUpdates,
                      source: "raw",
                      reason,
                      expectedHash,
                    })
                    toast.success(
                      t("adminDashboard.mcps.toastSavedRaw", {
                        defaultValue: "MCP server saved (raw)",
                      })
                    )
                    setEditingLabel(null)
                  } catch (err) {
                    toast.error(
                      t("adminDashboard.mcps.toastRawSaveFailed", {
                        defaultValue: "Raw save failed",
                      }),
                      { description: err instanceof Error ? err.message : undefined }
                    )
                    throw err
                  }
                }}
                onSubmit={async (updates) => {
                  try {
                    await updateMcpMutation.mutateAsync({ label: target.label, updates })
                    toast.success(
                      t("adminDashboard.mcps.toastUpdated", {
                        defaultValue: "MCP server updated",
                      })
                    )
                    setEditingLabel(null)
                  } catch (err) {
                    toast.error(
                      t("adminDashboard.mcps.toastUpdateFailed", {
                        defaultValue: "Failed to update MCP server",
                      }),
                      { description: err instanceof Error ? err.message : undefined }
                    )
                    throw err
                  }
                }}
              />
            )
          })()
        : null}

      <AccessPolicyDialog
        isLoading={isLoadingBaseAccessPolicy}
        isSaving={updateBaseAccessMutation.isPending}
        loadError={isBaseAccessPolicyError}
        open={isBaseAccessDialogOpen}
        policy={baseAccessPolicy}
        description={t("adminDashboard.mcps.baseAccessDescription", {
          defaultValue:
            "Default access policy applied to all MCP servers for this agent unless overridden per server.",
        })}
        title={t("adminDashboard.mcps.baseAccessTitle", {
          defaultValue: "Base access policy — MCP Servers",
        })}
        onOpenChange={setIsBaseAccessDialogOpen}
        onSave={async (policy) => {
          try {
            await updateBaseAccessMutation.mutateAsync({ agentName: selectedAgent, policy })
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw error
          }
        }}
      />

      <AccessPolicyDialog
        isLoading={isLoadingAccessPolicy}
        isSaving={updateAccessMutation.isPending || clearAccessMutation.isPending}
        loadError={isAccessPolicyError || isBaseAccessPolicyError}
        open={isAccessDialogOpen}
        policy={mcpAccessPolicy}
        description={t("adminDashboard.access.dialogDescription", {
          defaultValue: "Configure who can access this resource.",
        })}
        inherit={{
          enabled: true,
          inheritByDefault,
          policy: baseAccessPolicy,
          label: t("adminDashboard.mcps.inheritBaseAccess", {
            defaultValue: "Inherit access for all",
          }),
          description: t("adminDashboard.mcps.inheritBaseAccessDescription", {
            defaultValue:
              "When enabled, this server uses the section baseline access policy. Disable to override for this server.",
          }),
          onSaveInherited: async () => {
            if (!accessMcpLabel) return
            await clearAccessMutation.mutateAsync({
              agentName: selectedAgent,
              label: accessMcpLabel,
            })
            void queryClient.invalidateQueries({
              queryKey: ["admin", "agents", selectedAgent, "mcps"],
            })
          },
        }}
        title={t("adminDashboard.access.dialogTitle", {
          defaultValue: "Access policy — {{name}}",
          name: selectedMcp?.label ?? accessMcpLabel ?? "",
        })}
        onOpenChange={(open) => {
          setIsAccessDialogOpen(open)
          if (!open) {
            setAccessMcpLabel(undefined)
          }
        }}
        onSave={async (policy) => {
          if (!accessMcpLabel) return
          try {
            await updateAccessMutation.mutateAsync({
              agentName: selectedAgent,
              label: accessMcpLabel,
              policy,
            })
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw error
          }
        }}
      />

      <AddMcpDialog
        isOpen={isAddDialogOpen}
        isPending={addMutation.isPending}
        onOpenChange={setIsAddDialogOpen}
        onSubmit={handleAddMcp}
      />
    </>
  )
}
